import { Box, Container, Grid, Tooltip, Typography, CircularProgress } from "@mui/material"
import { Show, SimpleShowLayout, useNotify, useShowContext, useTranslate } from "react-admin"
import { CardCommon } from "src/components/uiElements"
import { CardCrudBottomButtons } from "src/components/uiElements/CRUD/CardCrudBottomButtons"
import { ShowListElement } from "src/components/uiElements/CRUD/ShowListElement"
import { ShowElement } from "src/components/uiElements/CRUD/ShowElement"
import { ItemsTable } from "../logic/ItemsTable"
import { Download, Refresh } from "@mui/icons-material"
import { useEffect, useState } from "react"
import { itemSupplierService } from 'src/services/reservation/itemSupplier/itemSupplier.service'
import { billingServices } from 'src/services/reservation/billing/billing.service'
import { supplierBillingServices } from 'src/services/purchases/supplier_billing/SupplierBilling.service'
import { LoadingButton } from '@mui/lab'
import { useErrorHandler } from 'src/hooks/useErrorHandler'

const ShowWrapper = ({ children }) => <Container maxWidth='false'> {children}</Container>

const ShowContent = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const showError = useErrorHandler()
  const { error, isLoading, record } = useShowContext()
  const [selectedItems, setSelectedItems] = useState([])
  const [itemsToSelect, setItemsToSelect] = useState([])
  const [supplierItems, setSupplierItems] = useState()
  const [retryLoading, setRetryLoading] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)

  const groupedInvoices = invoices
    .filter((invoice, index, self) => self.findIndex((item) => item.invoice_id === invoice.invoice_id) === index)
    .sort((a, b) => b.invoice_id - a.invoice_id)

  useEffect(()=>{
    if (record) {
      getSupplierItems()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record])

  useEffect(()=>{
    if (record?.id) {
      getSupplierBillingInvoices(record.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record])

  useEffect(()=>{
    if (record) {
      const initialitems = record?.supplier_billing_item_relation?.map(item => {
        const currency_relation = supplierItems?.find(elem => elem.id === item.item_supplier_relation?.id)?.currency_relation
        return {
          ...item.item_supplier_relation,
          supplier_name: record.supplier_name,
          supplier_payment_order_item_id: item.id,
          amount_pending: item.item_supplier_relation?.amount_pending,
          amount: item.amount,
          currency_relation
        }
      }) ?? []
      setSelectedItems(initialitems)
      setItemsToSelect(initialitems)
    }
  }, [record, supplierItems])

  const getSupplierItems = async () => {
    await itemSupplierService.getItemsSupplier({ supplier_id: record?.supplier_id }).then(res=>{
      setSupplierItems(res.data)
    })
  }

  const getSupplierBillingInvoices = async (supplierBillingId) => {
    setInvoicesLoading(true)
    try {
      const res = await supplierBillingServices.getInvoiceFromSupplierBilling(supplierBillingId)
      setInvoices(Array.isArray(res) ? res : [])
    } catch (error) {
      setInvoices([])
    } finally {
      setInvoicesLoading(false)
    }
  }

  const handleRetrySending = async () => {
    try {
      setRetryLoading(true)
      await billingServices.retrySupplierBillingInvoice(record?.id)
      notify('ra.notification.retry_supplier_billing_success', { type: 'success' })
    } catch (error) {
      handleRetrySendingError(error, () =>
        notify('ra.notification.retry_supplier_billing_error', { type: 'error' })
      )
    } finally {
      setRetryLoading(false)
    }
  }

  const handleRetrySendingError = (error, callback) => {
    const body = error?.response?.data
    if (body?.status_code === 418 && error?.response) {
      const detail = body?.data?.errors || body?.data?.data || body?.data
      error.response.status = 418
      error.response.data = {
        ...body,
        data: { errors: Array.isArray(detail) ? detail : [detail] },
      }
    }
    showError(error, callback)
  }

  const handleDownloadFile = (invoice) => {
      const url = invoice.attachment_url
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
  }

  if (isLoading) {
    return <div>{translate('ra.action.loading')}</div>
  }

  if (error) {
    return <div>{translate('ra.notification.http_error')}</div>
  }

  return (
    <SimpleShowLayout>

      <CardCommon title={translate('ra.title.supplier')}>
          <Grid container spacing={6}>
            <ShowListElement label={translate('ra.field.name')} value={record.supplier_name} />
            <ShowListElement label={translate('ra.field.code')} value={record.supplier_relation?.code} />
            <ShowListElement label={translate('ra.field.email')} value={record.supplier_relation?.email} />
          </Grid>
      </CardCommon>

      <CardCommon title={translate('ra.title.voucher_details')}>
          <Grid container spacing={6}>
            <ShowListElement
              label={translate('ra.field.document_type')}
              value={
                record.document_type
                  ? translate(`ra.field.${record.document_type}`)
                  : ''
              }
              xs={12}
              sm={6}
              md={3}
            />
            <ShowListElement
              label={translate('ra.field.document_number')}
              value={record.document_number}
              xs={12}
              sm={6}
              md={3}
            />
            <ShowListElement
              label={translate('ra.field.currency')}
              value={`${record.currency_relation?.symbol} ${record.currency_relation?.long_name} (${record.currency_relation?.short_name})`}
              xs={12}
              sm={6}
              md={3}
            />
            <ShowListElement xs={12} sm={6} md={3} label={translate('ra.field.date')} value={record.date} />
            <ShowListElement xs={12} sm={6} md={3} label={translate('ra.field.due_date')} value={record.due_date} />
            <Grid item xs={12} sm={6} md={3}>
              <Typography className='keyText' component='div' color='text.primary'>
                {translate('ra.field.status')}:
              </Typography>
              <ShowElement
                status={true}
                source='status'
                onlyValue
                style={{ padding: 0 }}
              />
            </Grid>
          </Grid>
      </CardCommon>

      <CardCommon
        title={translate('ra.title.registers')}
        action={
          <Tooltip placement='top' title={translate('ra.action.refresh')}>
            <Refresh
              color='primary'
              onClick={() => getSupplierBillingInvoices(record?.id)}
              style={{ cursor: 'pointer', fontSize: '1.1em' }}
            />
          </Tooltip>
        }
        headerStyle={{ paddingTop: '4px', paddingBottom: '4px' }}
      >
          <Grid container spacing={6} alignItems='left'>
            
            <Grid item xs={6} container justifyContent='flex-end' alignItems='center'>
              <LoadingButton
                loading={retryLoading}
                loadingPosition='start'
                variant='contained'
                onClick={handleRetrySending}
                disabled={
                  retryLoading ||
                  invoicesLoading ||
                  invoices.some((inv) => ['INVOICED', 'CONFIRMED'].includes(inv.status))
                }
              >
                {translate('ra.action.accounting_entry')}
              </LoadingButton>
            </Grid>
          </Grid>

          {invoicesLoading ? (
            <Box display='flex' justifyContent='center' py={3}>
              <CircularProgress />
            </Box>
          ) : groupedInvoices.map((invoice, index) => (
            <CardCommon
              key={invoice.invoice_id ?? index}
              customTitle={
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {`${translate('ra.title.register')} ${index + 1}`}
                  {invoice?.attachment_url && (
                    <Tooltip placement='top' title={translate('ra.action.download_file')}>
                      <Download
                        color='primary'
                        onClick={() => handleDownloadFile(invoice)}
                        style={{ cursor: 'pointer', fontSize: '1.1em', marginLeft: '0.25em' }}
                      />
                    </Tooltip>
                  )}
                </span>
              }
              headerStyle={{ paddingTop: '4px', paddingBottom: '4px' }}
              style={{ marginTop: 16 }}
            >
              <Grid container spacing={6}>
                <ShowListElement
                  label={translate('ra.field.billing_base')}
                  value={invoice.base}
                  sm={12}
                  md={3}
                />
                <ShowListElement label={translate('ra.field.accounting_date')} value={invoice.accounting_date} sm={12} md={3} />
                <ShowListElement label={translate('ra.field.external_id')} value={invoice.external_id} sm={12} md={3} />
                <Grid item xs={12} sm={6} md={3}>
                  <Typography className='keyText' component='div' color='text.primary'>
                    {translate('ra.field.status')}:
                  </Typography>
                  <ShowElement
                    status={true}
                    data={invoice.status}
                    onlyValue
                    style={{ padding: 0 }}
                  />
                </Grid>
              </Grid>
            </CardCommon>
          ))}
      </CardCommon>
      
      <CardCommon title={translate('ra.title.item_selected')} actions={<CardCrudBottomButtons btnEdit={true} />}>
        <ItemsTable
          isShow
          enableFilters={false}
          supplierId={record?.supplier_id}
          selectedItems={itemsToSelect}
          setSelectedItems={setItemsToSelect}
          editData={selectedItems}
          hiddenColumns={['amount_paid', 'amount_pending']}
        />
      </CardCommon>

    </SimpleShowLayout>
  )
}

export const SupplierBillingShow = (props) => {
  return (
    <Show component={ShowWrapper} {...props} actions={null} className='CrudShowContainer'>
      <ShowContent {...props} />
    </Show>
  )
}